import React from 'react';
import styled from 'styled-components';
import { IRadioItemProps } from './IRadioItemProps';
import { BodyText } from '../Typography';
import { Grid, GridItem } from '../Grid';
import { mergeTests } from '../../test-utilities/mergeTests';
import { InputStyled } from '../TextInput';

const radioButtonSize = '16px';
const StyledRadioListInput = styled(InputStyled)<IRadioItemProps>(({ theme, checked, showErrorHighlight }) => ({
  appearance: 'none',
  padding: 'unset',
  borderRadius: '50%',
  cursor: 'pointer',
  display: 'inline-block',
  minWidth: radioButtonSize,
  height: radioButtonSize,
  minHeight: radioButtonSize,
  margin: `0 ${theme.spacing.spacing4} 0 0`,
  width: radioButtonSize,
  '&:hover': {
    borderRadius: '50%',
  },
  '&:read-only:not(div)': {
    backgroundColor: theme.colors.defaultContentBackground,
    border: `solid ${checked ? '5px' : '1px'} ${
      checked
        ? theme.colors.defaultContentText
        : showErrorHighlight
          ? theme.colors.error
          : theme.colors.secondarySelectedBackground
    }`,
    '&:hover': {
      borderColor: checked ? theme.colors.black : theme.colors.gray300,
      backgroundColor: checked ? theme.colors.defaultContentBackground : theme.colors.gray100,
    },
    '&:focus': {
      border: `2px solid ${theme.colors.primary}`,
      background: !checked
        ? theme.colors.gray200
        : `radial-gradient(
        circle at center,
        ${theme.colors.defaultContentBackground} 0,
        ${theme.colors.defaultContentBackground} 38%,
        ${theme.colors.defaultContentText} 0%,
        ${theme.colors.defaultContentText} 16%
      )`,
    },
  },
}));

const StyledRadioListLabel = styled(Grid)`
  flex-grow: 1;
  align-items: center;
  cursor: pointer;
  column-gap: ${(props) => props.theme.spacing.spacing3};
  row-gap: ${(props) => props.theme.spacing.spacing1};
`;

const StyledRadioListTitle = styled(BodyText)`
  color: ${(props) => props.theme.colors.gray700};
`;

const StyledRadioListValue = styled(BodyText)`
  color: ${(props) => props.theme.colors.gray500};
`;

export const RadioListItem: React.FC<IRadioItemProps> = (props) => {
  const { id, label, hintText } = props;
  const dataTestClass = props['data-test-class'] || 'radio-list-item';
  return (
    <StyledRadioListLabel
      as="label"
      templateColumns="min-content max-content"
      htmlFor={id}
      data-test-class={mergeTests(dataTestClass, 'label')}
    >
      <StyledRadioListInput {...props} type="radio" data-test-class={mergeTests(dataTestClass, 'input')} />
      {label && (
        <StyledRadioListTitle data-test-class={mergeTests(dataTestClass, 'text')}> {label} </StyledRadioListTitle>
      )}
      <GridItem columnStart={2}>
        {hintText && (
          <StyledRadioListValue data-test-class={mergeTests(dataTestClass, 'text')}> {hintText} </StyledRadioListValue>
        )}
      </GridItem>
    </StyledRadioListLabel>
  );
};
